import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { getFeed, getFeedItem, getNewItemsCount } from '@/services/feed';
import { parseApiResponse } from '@/utils/apiResponseParser';
import { useOnboardingStore } from '@/stores/onboarding';
import { FEED_SPORTS } from '@/config/sports';
import { FEED_ITEM_TYPES } from '@/config/feedItemTypes';
import { Sentry } from '@/services/sentry';

const PAGE_SIZE = 20;

export const useFeedStore = defineStore('feed', () => {
  const items = ref([]);
  const isLoading = ref(false);
  const error = ref(null);
  const hasMorePages = ref(true);
  const newItemsCount = ref(0);

  const onboardingStore = useOnboardingStore();

  const selectedTypes = computed(() => {
    return onboardingStore.feedTypes
      .filter((feedType) => feedType.enabled)
      .map((feedType) => feedType.name);
  });

  const selectedSports = computed(() => {
    return FEED_SPORTS
      .filter((feedSport) => {
        const onboardingSport = onboardingStore.sports.find((sport) => sport.name === feedSport.name);
        return onboardingSport?.enabled;
      })
      .map((feedSport) => feedSport.id);
  });

  const getFilterParams = () => {
    const params = {};

    if (selectedTypes.value.length > 0) {
      params.types = selectedTypes.value;
    }

    if (selectedSports.value.length > 0) {
      params.sports = selectedSports.value;
    }

    return params;
  };

  const fetchItemById = async (id) => {
    return await getFeedItem(Number(id));
  };

  const loadFeed = async (beforeId) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await getFeed({ itemsPerPage: PAGE_SIZE, beforeId, ...getFilterParams() });
      const data = parseApiResponse(response);

      if (!Array.isArray(data)) {
        throw new Error('API response is not an array');
      }

      error.value = null;

      if (data.length < PAGE_SIZE) {
        hasMorePages.value = false;
      }

      if (beforeId === undefined) {
        items.value = data;
      } else {
        const existingIds = new Set(items.value.map(item => item.id));
        const newItems = data.filter(item => !existingIds.has(item.id));

        items.value = [...items.value, ...newItems];
      }

      return data;
    } catch (err) {
      error.value = err.message || 'Failed to load feed';

      console.error('Error loading feed:', err);
      Sentry.captureException(err);
    } finally {
      isLoading.value = false;
    }
  };

  const loadMore = async () => {
    if (!hasMorePages.value || isLoading.value || items.value.length === 0) {
      return;
    }

    const oldestId = Math.min(...items.value.map(item => Number(item.id)));

    return await loadFeed(oldestId);
  };

  const refresh = async () => {
    hasMorePages.value = true;
    newItemsCount.value = 0;

    return await loadFeed();
  };

  const reset = () => {
    items.value = [];
    itemsById.value = new Map();
    newItemsCount.value = 0;
    hasMorePages.value = true;
    error.value = null;
  };

  const checkForNewItems = async () => {
    if (items.value.length === 0 || isLoading.value) {
      return;
    }

    try {
      const newestId = Math.max(...items.value.map(item => Number(item.id)));
      const response = await getNewItemsCount(newestId, getFilterParams());

      newItemsCount.value = response.count;
    } catch (err) {
      console.error('Error checking for new items:', err);
      Sentry.captureException(err);
    }
  };

  const applyFilters = async ({ types, sports }) => {
    onboardingStore.updateFeedTypes(
      onboardingStore.feedTypes.map((feedType) => ({
        ...feedType,
        enabled: types.includes(feedType.name),
      }))
    );

    onboardingStore.updateSports(
      onboardingStore.sports.map((sport) => {
        const feedSport = FEED_SPORTS.find((filterSport) => filterSport.name === sport.name);

        if (feedSport) {
          return { ...sport, enabled: sports.includes(feedSport.id) };
        }

        return sport;
      })
    );

    await onboardingStore.submitOnboarding();

    items.value = [];
    hasMorePages.value = true;
    newItemsCount.value = 0;

    return await loadFeed();
  };

  const resetFilters = async () => {
    const allTypes = FEED_ITEM_TYPES.map((itemType) => itemType.type);
    const allSports = FEED_SPORTS.map((sport) => sport.id);

    return await applyFilters({ types: allTypes, sports: allSports });
  };


  const clearItemsCache = () => {
    itemsById.value = new Map();
  };

  return {
    items,
    isLoading,
    error,
    hasMorePages,
    newItemsCount,
    selectedTypes,
    selectedSports,
    fetchItemById,
    loadFeed,
    loadMore,
    refresh,
    reset,
    checkForNewItems,
    applyFilters,
    resetFilters,
    clearItemsCache
  };
});
